import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteHistoryIcon } from 'shared/assets/svg/DeleteHistoryIcon';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'shared/components/ui/alert-dialog';
import activityPanelStore from 'shared/store/activityPanelStore';
import useToggleMenu from 'shared/layout/useToggleMenu';
import colorsStore from 'shared/store/colorsStore';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';
import { cn } from 'utils/misc';

import { MappedHistoryItem } from 'types/AssistantTypes';

import styles from './HistoryItem.module.scss';

type Props = {
  thread: MappedHistoryItem;
  onThreadDelete?: (threadId: string) => void;
  sizeExpanded: number;
};

export const HistoryItem: React.FC<Props> = ({ thread, onThreadDelete, sizeExpanded }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const isTabletOrSmaller = useTabletAndBelowMediaQuery();

  const { t } = useTranslation();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [deleted, setDeleted] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const toggleMenu = useToggleMenu();

  const onDelete = (e: any) => {
    e.stopPropagation();
    setShowDeleteButton(false);
    setShowDeleteDialog(false);
    setDeleted(true);
    onThreadDelete?.(thread.id);
  };

  const handleShowDeleteDialog = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDialogCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowDeleteButton(false);
    setShowDeleteDialog(false);
  };

  if (deleted) {
    return null;
  }

  const isActive = threadId === thread?.id;
  return (
    <div
      className={cn('duration-150', styles.historyItem, { [styles.active]: isActive })}
      style={{
        width: sizeExpanded - 40,
        backgroundColor: isActive ? colorsConfig.finecoBlueLight : '',
        color: isActive ? colorsConfig.finecoBluePrimary : colorsConfig.finecoBlack,
      }}
      onMouseEnter={(e) => {
        setShowDeleteButton(true);
        e.currentTarget.style.color = isActive ? colorsConfig.finecoBluePrimary : colorsConfig.finecoBluePrimary;
      }}
      onMouseLeave={(e) => {
        setShowDeleteButton(false);
        e.currentTarget.style.color = isActive ? colorsConfig.finecoBluePrimary : colorsConfig.finecoBlack;
      }}
      onClick={() => {
        if (isActive) return;
        activityPanelStore.getState().resetStore();
        navigate(`/assistant/${thread.id}`);
        if (isTabletOrSmaller) {
          toggleMenu();
        }
      }}
    >
      {typeof thread.title === 'string'
        ? thread.title
        : thread.title.map((part, index: number) => {
            if (part.highlight) {
              return (
                <span
                  key={index}
                  style={{ backgroundColor: colorsConfig.yellow, borderRadius: '2px', padding: '2px 0px' }}
                >
                  {part.text}
                </span>
              );
            }
            return <span key={index}>{part.text}</span>;
          })}
      <div
        className={styles.deleteButtonContainer}
        style={{ color: colorsConfig.threadHistoryCopy }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = isActive ? '1' : '0.5';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = isActive ? '0.5' : '1';
        }}
      >
        <AlertDialog>
          {showDeleteButton && (
            <AlertDialogTrigger onClick={handleShowDeleteDialog}>
              <div className={styles.deleteButton}>
                <DeleteHistoryIcon className={styles.deleteIcon} />
              </div>
            </AlertDialogTrigger>
          )}
          {showDeleteDialog && (
            <AlertDialogContent
              style={{
                backgroundColor: colorsConfig.popupBackground
                  ? colorsConfig.popupBackground
                  : colorsConfig.rootLayoutBackground,
                color: colorsConfig.threadHistoryCopy,
              }}
            >
              <AlertDialogHeader>
                <AlertDialogTitle className={styles.dialogTitle}>{t('assistant.deleteThread')}</AlertDialogTitle>
              </AlertDialogHeader>
              {t('assistant.deleteThreadDesc')}
              <AlertDialogFooter>
                <AlertDialogCancel style={{ color: colorsConfig.threadHistoryCopy }} onClick={handleDialogCancel}>
                  {t('global.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  style={{ backgroundColor: colorsConfig.red, color: colorsConfig.white }}
                  onClick={onDelete}
                >
                  {t('global.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          )}
        </AlertDialog>
      </div>
    </div>
  );
};
